/* eslint-env browser */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useTranslation } from 'react-i18next';
import { tokens as c } from '../../theme';

// Filter chips as the approved W2_Portal_Drivers board draws them (taken
// from views/Drivers.js): every choice is a 44-px chip with its count, a
// check when selected (never colour alone); less-used filters are one
// "Label: value ▾" chip with a menu; active filters show as removable chips
// next to the count with one "Clear all".
//
//   <ChipGroup label="Contacts" value={contacts} onChange={setContacts}
//     options={[{ value: 'all', label: 'All' }, { value: 'invited', label: 'Invited contacts', count: 120 }]} />
//   <DropChip label="Licence" value={licence} onChange={setLicence} options={[...]} />
//   <ToggleChip label="App-less" count={42} selected={appless} onClick={() => setAppless(!appless)} />
//   <ActiveFilters tokens={[{ key: 'contacts', group: 'Contacts', value: 'Invited contacts' }]}
//     onRemove={(key) => ...} onClearAll={...} />
//
// Counts are numbers (formatted 12,340) or ready text (countText). They are
// totals over what is loaded, so every group adds up to the same total.

export const ALL = 'all';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

const chipSx = (selected) => ({
  height: 44,
  borderRadius: '22px',
  px: 0.75,
  fontSize: 15,
  fontWeight: 600,
  bgcolor: selected ? c.ACCENT_TINT : c.SURFACE,
  color: selected ? c.ACCENT_TEXT : c.INK,
  border: `${selected ? 1.5 : 1}px solid ${selected ? c.ACCENT : c.OUTLINE_STRONG}`,
  '& .MuiChip-icon': { color: c.ACCENT_TEXT, fontSize: 18, ml: 0.75, mr: -0.25 },
  '& .MuiChip-label': { px: 1 },
  '&.MuiChip-clickable:hover': { bgcolor: selected ? c.ACCENT_TINT : c.SURFACE_ALT },
  '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
});

/** One chip: 44 px, its count beside the words, a check when selected. */
export function FilterChip({ selected, label, count, countText, dot, icon, onClick, disabled }) {
  const shownCount = countText !== undefined && countText !== null ? countText : count !== undefined && count !== null ? fmt(count) : null;
  return (
    <Chip
      clickable
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected ? 'true' : 'false'}
      icon={
        selected ? (
          <CheckRoundedIcon />
        ) : dot ? (
          <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot, flex: 'none' }} />
        ) : (
          icon || undefined
        )
      }
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
          <span>{label}</span>
          {shownCount !== null ? (
            <Box component="span" sx={{ fontWeight: 400, color: selected ? c.ACCENT_TEXT : c.INK_2, fontVariantNumeric: 'tabular-nums' }}>
              {shownCount}
            </Box>
          ) : null}
        </Box>
      }
      sx={chipSx(selected)}
    />
  );
}

/** A labelled row of chips where exactly one is selected (the first option is usually "All"). */
export function ChipGroup({ label, value, options, onChange, sx }) {
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, ...(sx || {}) }}>
      {label ? (
        <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: c.INK_2, lineHeight: 1.4, mr: 0.5 }}>
          {label}
        </Typography>
      ) : null}
      {(options || []).map((o) => (
        <FilterChip
          key={String(o.value)}
          selected={value === o.value}
          label={o.label}
          count={o.count}
          countText={o.countText}
          dot={o.dot}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        />
      ))}
    </Box>
  );
}

/** A single on/off chip ("App-less 42"). */
export function ToggleChip({ label, count, countText, selected, onClick, dot, icon, disabled }) {
  return <FilterChip selected={selected} label={label} count={count} countText={countText} dot={dot} icon={icon} onClick={onClick} disabled={disabled} />;
}

/** "Licence: All ▾": a less-used filter as one chip with a menu of its choices. */
export function DropChip({ label, value, options, onChange, allValue = ALL }) {
  const [anchor, setAnchor] = useState(null);
  const list = options || [];
  const current = list.find((o) => o.value === value) || list[0] || { label: '' };
  const active = value !== allValue;
  return (
    <>
      <Chip
        clickable
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : 'false'}
        icon={active ? <CheckRoundedIcon /> : undefined}
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <span>{label}:</span>
            <Box component="span" sx={{ fontWeight: active ? 700 : 400 }}>
              {current.label}
            </Box>
            <ExpandMoreRoundedIcon aria-hidden="true" sx={{ fontSize: 18, ml: 0.25 }} />
          </Box>
        }
        sx={{ ...chipSx(active), px: 0.5 }}
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {list.map((o) => (
          <MenuItem
            key={String(o.value)}
            selected={o.value === value}
            onClick={() => {
              setAnchor(null);
              onChange(o.value);
            }}
            sx={{ minWidth: 220, gap: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {o.value === value ? <CheckRoundedIcon fontSize="small" sx={{ color: c.ACCENT_TEXT }} /> : null}
            </ListItemIcon>
            <ListItemText primary={o.label} />
            {o.count !== undefined && o.count !== null ? (
              <Typography variant="body2" sx={{ color: c.INK_2, ml: 2, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(o.count)}
              </Typography>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The active filters as removable chips ("Contacts: Invited contacts ×"),
 * optionally after a count line, with one "Clear all" when any is set.
 * tokens: [{ key, group, value }]; onRemove(key).
 */
export function ActiveFilters({ countText, tokens, onRemove, onClearAll, children, sx }) {
  const { t } = useTranslation();
  const list = Array.isArray(tokens) ? tokens : [];
  if (!countText && list.length === 0 && !children) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, ...(sx || {}) }}>
      {countText ? (
        <Typography role="status" aria-live="polite" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, lineHeight: 1.4, mr: 0.5 }}>
          {countText}
        </Typography>
      ) : null}
      {countText && list.length ? <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} /> : null}
      {list.map((tk) => (
        <Chip
          key={tk.key}
          aria-label={t('drivers_remove_filter', { label: `${tk.group}: ${tk.value}`, defaultValue: 'Remove filter {{label}}' })}
          label={
            <span>
              {tk.group ? `${tk.group}: ` : ''}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {tk.value}
              </Box>
            </span>
          }
          onDelete={() => onRemove && onRemove(tk.key)}
          deleteIcon={<CloseRoundedIcon />}
          sx={{
            height: 36,
            borderRadius: '18px',
            bgcolor: c.SURFACE_ALT,
            color: c.INK,
            fontSize: 14,
            fontWeight: 400,
            '& .MuiChip-deleteIcon': { color: c.INK_2, bgcolor: c.SURFACE, borderRadius: '50%', fontSize: 20, p: '2px' },
            '& .MuiChip-deleteIcon:hover': { color: c.INK },
          }}
        />
      ))}
      {list.length && onClearAll ? (
        <Button variant="text" onClick={onClearAll}>
          {t('clear_all', { defaultValue: 'Clear all' })}
        </Button>
      ) : null}
      {children}
    </Box>
  );
}
